import React, { useEffect, useRef, useState } from 'react';
import { MemoizedFilters } from './Filters';
import Map from './Map';
import Loading from 'common/components/Loading';
import { MemoizedCommunityCard } from './CommunityCard';
import MapAccordion from './MapAccordion';
import LotListing from './LotListing';
import LotCard from './LotCard';
import useMapsStore, { usePrintEventStore } from '../store/useMapsStore';
import useLotsStore from '../store/useLotsStore';
import usePolygonsStore from '../store/usePolygonsStore';
import useFiltersStore from '../store/useFiltersStore';
import { handleGetGeoJson } from '../util/api-helpers';
import { attachSortStateListener } from '../util/sort';
import { attachFilterStateListener } from '../util/filters';
import { getShapeCenter } from '../util/polygon';
import { useWindowSize } from 'common/util/hooks';
import { swapCoordinatesArr } from 'common/util/helpers';
import settings from '../util/config';
import { MAP_DEFAULT_LAT, MAP_DEFAULT_LONG, MIN_DESKTOP_BREAKPOINT, POLYGON_TYPE_LOT, POLYGON_TYPE_COMMUNITY, POLYGON_DEFAULT_ZOOM } from '../util/constants';
import DetailLotCard from './detail-lot-card/DetailLotCard';
import Pagination from 'common/components/Pagination';
import MapShapes from './map-shapes/MapShapes';
const ViewportMapBlock = () => {
    const { lotListExpanded, setLotListExpanded, brand, setBrand, setCommunityId, zoom, setZoom } = useMapsStore();
    const { setPolygons, community, setCommunity } = usePolygonsStore(["setPolygons", "community", "setCommunity"]);
    const { setLots, filteredLots } = useLotsStore(["setLots", "filteredLots"]);
    const { setApplyFilterFlag } = useFiltersStore();
    const { isPrintMapRendering, setIsPrintMapRendering, isPrintButtonLoading, setIsPrintButtonLoading } = usePrintEventStore();
    const [vw, vh] = useWindowSize();
    const [geoJsonFetchDone, setGeoJsonFetchDone] = useState(false);
    const [mapCenter, setMapCenter] = useState({ lat: MAP_DEFAULT_LAT, lng: MAP_DEFAULT_LONG });
    const [isMobile, setIsMobile] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);
    const URLcommunityId = urlParams.get('communityId') ?? undefined;
    const URLbrand = urlParams.get('brand') ?? '';
    const communityCardRef = useRef(null);
    const filtersRef = useRef(null);
    const lotListingFilterBarRef = useRef(null);
    // run once after render
    useEffect(() => {
        // only call the API if the community & brand are setup properly
        if (URLcommunityId && URLbrand) {
            setBrand(URLbrand);
            setCommunityId(URLcommunityId);
            processGeoInfo();
        }
        const dettachSortStateListener = attachSortStateListener();
        const dettachFilterStateListener = attachFilterStateListener();
        // Unsubscribe state listeners on unmount
        return () => {
            dettachSortStateListener();
            dettachFilterStateListener();
        };
    }, []);
    // run directly after vw changes
    useEffect(() => {
        if (vw && vw < MIN_DESKTOP_BREAKPOINT && !isMobile) {
            setIsMobile(true);
            setLotListExpanded(false);
        }
        else if (vw && vw >= MIN_DESKTOP_BREAKPOINT && isMobile) {
            setIsMobile(false);
            setLotListExpanded(true);
        }
    }, [vw]);
    useEffect(() => {
        // set css properties for component height to be used in responsive styling
        const filtersHeight = (filtersRef.current?.offsetHeight ?? 0) + 'px';
        const communityCardHeight = (communityCardRef.current?.offsetHeight ?? 0) + 'px';
        const lotListingFilterBarHeight = (lotListingFilterBarRef.current?.offsetHeight ?? 0) + 'px';
        document.documentElement.style.setProperty('--vm-filters-height', filtersHeight);
        document.documentElement.style.setProperty('--vm-community-card-height', communityCardHeight);
        document.documentElement.style.setProperty('--vm-lot-listing-filter-bar-height', lotListingFilterBarHeight);
    });
    function handlePrintClick() {
        setIsPrintButtonLoading(true);
        if (community?.Coordinates) {
            const communityCenter = getShapeCenter(community.Coordinates);
            setMapCenter(communityCenter);
            setZoom(community?.Properties.Zoom > 0 ? community?.Properties.Zoom : POLYGON_DEFAULT_ZOOM);
        }
        // kick off the secondary print map rendering by setting the global state
        setIsPrintMapRendering(true);
    }
    async function processGeoInfo() {
        setGeoJsonFetchDone(false);
        const geoFeatures = await handleGetGeoJson(useMapsStore.getState().brand, useMapsStore.getState().communityId);
        if (geoFeatures === -1) {
            console.error('Error with getting geoJson data');
        }
        else {
            geoFeatures.map((item) => {
                item.Coordinates = swapCoordinatesArr(item.Coordinates);
                return item;
            });
            setPolygons(geoFeatures);
            //centering the map relative to the polygon
            let community = geoFeatures.find((feature) => feature.Properties.Type === POLYGON_TYPE_COMMUNITY && !feature.Properties.ParentCommunityId);
            if (!community) {
                community = geoFeatures.find((feature) => feature.Properties.Type === POLYGON_TYPE_COMMUNITY);
            }
            setCommunity(community ?? null);
            if (community?.Properties?.CenterLatitude && community.Properties?.CenterLongitude) {
                setMapCenter({ lat: +community?.Properties?.CenterLatitude, lng: +community.Properties?.CenterLongitude });
            }
            else if (community?.Coordinates) {
                const communityCenter = getShapeCenter(community.Coordinates);
                setMapCenter(communityCenter);
            }
            if (community?.Properties?.Zoom && community.Properties.Zoom > 0) {
                setZoom(community.Properties.Zoom);
            }
            else {
                setZoom(POLYGON_DEFAULT_ZOOM);
            }
            const lots = geoFeatures.filter((feature) => feature.Properties.Type === POLYGON_TYPE_LOT);
            setLots(lots);
            // set available lots & filtered polygons
            setApplyFilterFlag(true);
        }
        setGeoJsonFetchDone(true);
    }
    return URLcommunityId && URLbrand ? (React.createElement("section", { className: `viewport-map ${brand}` },
        React.createElement(MemoizedFilters, { filtersRef: filtersRef, isMobile: isMobile }),
        React.createElement("div", { className: 'viewport-map__main-content' + (lotListExpanded ? ' lot-list-expanded' : '') },
            isMobile && React.createElement(MemoizedCommunityCard, { communityCardRef: communityCardRef }),
            brand && !geoJsonFetchDone ? (React.createElement(Loading, { message: "Loading Map" })) : (React.createElement(Map, { center: mapCenter },
                React.createElement(MapShapes, { isMobile: isMobile }),
                React.createElement("button", { className: "viewport-map__print-btn cm__loading-spinner", "aria-label": "Print Map", type: "button", "data-spinner-on": isPrintButtonLoading + '', disabled: isPrintButtonLoading, onClick: handlePrintClick }))),
            isPrintMapRendering && (React.createElement(Map, { center: mapCenter, className: "viewport-map__print-map-template", removeControls: true, printOnLoad: true },
                React.createElement(MapShapes, { isMobile: isMobile }))),
            React.createElement(MapAccordion, null,
                !isMobile && React.createElement(MemoizedCommunityCard, { communityCardRef: communityCardRef }),
                React.createElement(LotListing, { lotListingFilterBarRef: lotListingFilterBarRef, onPrintClick: handlePrintClick, isMobile: isMobile },
                    !isMobile &&
                        filteredLots.map((lot, index) => {
                            return React.createElement(LotCard, { key: `desktop-${index}`, lot: lot });
                        }),
                    isMobile && (React.createElement(Pagination, { navContainerLabel: settings.MobilePaginationNavigationLabel, pageLabelPrefix: settings.MobilePaginationPagePrefixLabel, prevPageLabel: settings.MobilePaginationPrevLabel, nextPageLabel: settings.MobilePaginationNextLabel, perPage: 6 }, filteredLots.map((lot, index) => {
                        return (React.createElement(DetailLotCard, { key: `mobile-${index}`, isMobile: true, polygon: lot, onClose: () => { } }));
                    })))))))) : (React.createElement("h2", { className: "viewport-map-block__error" }, settings.CommunityNotFoundLabel));
};
export default React.memo(ViewportMapBlock);
